// Resolve proposal PDF section copy from the page library when available,
// falling back to format-scoped text overrides (legacy).

import { listProposalPages } from "./proposalPageClient";
import {
  PRIMARY_PAGE_KEYS,
  sandboxBlocksFromPageBody,
  sectionsFromPageBody,
  type ProposalPage,
  type ProposalPageArchetype,
} from "./proposalPageTypes";
import {
  PROPOSAL_TEXT_SECTION_KEYS,
  getProposalTextDefault,
  type ProposalTextSectionKey,
} from "./proposalTextSections";
import {
  resolveProposalSections,
  type ResolvedProposalSections,
} from "./proposalTextResolver";
import type { OpportunityFormatKey } from "@/types/opportunity";
import {
  isBuiltinProposalPageKind,
  type ProposalAlternatePage,
  type ProposalBuiltinPageKind,
} from "./proposalAlternateTypes";
import { applyAlternatePagesToSections } from "./proposalAlternateApply";
import type { ProposalSandboxBlock } from "./proposalSandboxBlocks";
import { defaultBodyForArchetype } from "./proposalPageBodyOps";

function emptySections(formatKey: OpportunityFormatKey): ResolvedProposalSections {
  const result = {} as ResolvedProposalSections;
  for (const key of PROPOSAL_TEXT_SECTION_KEYS) {
    result[key] = { body: getProposalTextDefault(formatKey, key), included: true };
  }
  return result;
}

function mergePageIntoSections(
  sections: ResolvedProposalSections,
  page: ProposalPage,
): void {
  const partial = sectionsFromPageBody(page.archetype, page.body);
  for (const [key, value] of Object.entries(partial)) {
    if (!value) continue;
    sections[key as ProposalTextSectionKey] = value;
  }
}

function pickPrimaryPages(pages: ProposalPage[]): Map<ProposalPageArchetype, ProposalPage> {
  const byArchetype = new Map<ProposalPageArchetype, ProposalPage>();
  for (const page of pages) {
    if (page.page_key === PRIMARY_PAGE_KEYS[page.archetype]) {
      byArchetype.set(page.archetype, page);
    }
  }
  for (const page of pages) {
    if (!byArchetype.has(page.archetype)) {
      byArchetype.set(page.archetype, page);
    }
  }
  return byArchetype;
}

function defaultBlocksForKind(
  formatKey: OpportunityFormatKey,
  kind: ProposalBuiltinPageKind,
): ProposalSandboxBlock[] {
  return sandboxBlocksFromPageBody(kind, defaultBodyForArchetype(formatKey, kind));
}

export type ResolvedProposalPackageContent = {
  sections: ResolvedProposalSections;
  pageTitles: Partial<Record<ProposalBuiltinPageKind, string>>;
  /** Ordered sandbox blocks per builtin page kind included in the package. */
  pageBlocks: Partial<Record<ProposalBuiltinPageKind, ProposalSandboxBlock[]>>;
  /** @deprecated Prefer pageBlocks.intent_to_purchase */
  intentBlocks: ProposalSandboxBlock[];
};

export async function resolveProposalSectionsForPackage(args: {
  formatKey: OpportunityFormatKey;
  productType: string;
  pages: readonly ProposalAlternatePage[];
}): Promise<ResolvedProposalPackageContent> {
  const { formatKey, productType, pages } = args;

  const libraryRes = await listProposalPages({
    format_key: formatKey,
    product_type: productType,
    ensure: true,
  });

  const librarySupported = libraryRes.ok && libraryRes.data.pages_supported !== false;
  const libraryPages = libraryRes.ok ? libraryRes.data.pages : [];

  if (!librarySupported || libraryPages.length === 0) {
    const legacy = await resolveProposalSections({ formatKey, productType });
    const intentBlocks = defaultBlocksForKind(formatKey, "intent_to_purchase");
    return {
      sections: applyAlternatePagesToSections(legacy, pages),
      pageTitles: {},
      pageBlocks: { intent_to_purchase: intentBlocks },
      intentBlocks,
    };
  }

  const byId = new Map(libraryPages.map((p) => [p.id, p]));
  const primaries = pickPrimaryPages(libraryPages);
  const sections = emptySections(formatKey);
  const pageTitles: Partial<Record<ProposalBuiltinPageKind, string>> = {};
  const pageBlocks: Partial<Record<ProposalBuiltinPageKind, ProposalSandboxBlock[]>> = {};

  const builtins = pages.filter(
    (p): p is ProposalAlternatePage & { page_kind: ProposalBuiltinPageKind } =>
      isBuiltinProposalPageKind(p.page_kind),
  );

  for (const page of builtins) {
    if (!page.included) continue;
    const fromId =
      page.proposal_page_id && byId.get(page.proposal_page_id)
        ? byId.get(page.proposal_page_id)!
        : primaries.get(page.page_kind);
    if (!fromId) continue;
    mergePageIntoSections(sections, fromId);
    const label = fromId.label?.trim();
    if (label) pageTitles[page.page_kind] = stripPrimarySuffix(label);
    pageBlocks[page.page_kind] = sandboxBlocksFromPageBody(fromId.archetype, fromId.body);
  }

  const intentBlocks =
    pageBlocks.intent_to_purchase ?? defaultBlocksForKind(formatKey, "intent_to_purchase");

  return {
    sections: applyAlternatePagesToSections(sections, pages),
    pageTitles,
    pageBlocks,
    intentBlocks,
  };
}

function stripPrimarySuffix(label: string): string {
  return label.replace(/\s*\(Primary\)\s*$/i, "").trim() || label;
}
